import * as React from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LocaleProvider, ThemeProvider, ToastProvider } from '@ministry/ui/internal';

import { useStoredLocale } from './lib/use-stored-locale.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { AppShell } from './components/AppShell.js';
import { RequireAuth } from './components/RequireAuth.js';
import { LoginPage } from './routes/LoginPage.js';
import { MfaPage } from './routes/MfaPage.js';
import { MfaEnrolPage } from './routes/MfaEnrolPage.js';
import { PasswordResetRequestPage } from './routes/PasswordResetRequestPage.js';
import { PasswordResetPage } from './routes/PasswordResetPage.js';
import { DashboardPage } from './routes/DashboardPage.js';
import { FileRegisterPage } from './routes/FileRegisterPage.js';
import { FileIntakePage } from './routes/FileIntakePage.js';
import { FileDetailPage } from './routes/FileDetailPage.js';
import { MyTasksPage } from './routes/MyTasksPage.js';
import { AppointmentInboxPage } from './routes/AppointmentInboxPage.js';
import { AppointmentDetailPage } from './routes/AppointmentDetailPage.js';
import { EntranceVerificationPage } from './routes/EntranceVerificationPage.js';
import { MyAvailabilityPage } from './routes/MyAvailabilityPage.js';
import { AvailabilityAdminPage } from './routes/AvailabilityAdminPage.js';
import { NotificationCentrePage } from './routes/NotificationCentrePage.js';
import { AccountSettingsPage } from './routes/AccountSettingsPage.js';
import { NotFoundPage } from './routes/NotFoundPage.js';
import {
  OrgUnitsPage,
  PlatformAuditPage,
  TenantsPage,
  UserCreatePage,
  UserDetailPage,
  UserDirectoryPage,
  UserSessionsPage,
} from './routes/admin/index.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // AUTH_REQUIRED/validation failures are not worth retrying automatically; a
      // network blip is handled by the explicit retry actions this app's screens render
      // themselves (never a silent background retry the user cannot see).
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

export function App(): React.JSX.Element {
  const { locale, setLocale } = useStoredLocale();
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <LocaleProvider locale={locale} onLocaleChange={setLocale}>
          <ThemeProvider>
            {/*
              CLAUDE.md brief: "No toast system ... one mechanism, not a banner here and
              inline text there." `ToastProvider`/`useToast` (`@ministry/ui`) already
              existed, fully built and tested, but was never exported from this
              package's `/internal` entry point and so was never mounted anywhere in
              this app — every screen had to invent its own ad-hoc success/error
              affordance instead (an inline span that never clears on My Availability,
              a dismissible banner on Account Settings, nothing at all after a file
              action modal closes). One provider, mounted once, above the router so
              every route (including the pre-auth ones) can call `useToast()`.
            */}
            <ToastProvider>
              <BrowserRouter>
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/mfa" element={<MfaPage />} />
                  <Route path="/enrol/mfa" element={<MfaEnrolPage />} />
                  <Route path="/reset-password/request" element={<PasswordResetRequestPage />} />
                  <Route path="/reset-password" element={<PasswordResetPage />} />
                  <Route element={<RequireAuth />}>
                    <Route element={<AppShell />}>
                      <Route path="/dashboard" element={<DashboardPage />} />
                      <Route path="/files" element={<FileRegisterPage />} />
                      <Route path="/files/new" element={<FileIntakePage />} />
                      <Route path="/files/:fileId" element={<FileDetailPage />} />
                      <Route path="/tasks" element={<MyTasksPage />} />
                      <Route path="/appointments" element={<AppointmentInboxPage />} />
                      <Route path="/appointments/verify" element={<EntranceVerificationPage />} />
                      <Route path="/appointments/:appointmentId" element={<AppointmentDetailPage />} />
                      <Route path="/availability" element={<MyAvailabilityPage />} />
                      <Route path="/availability/admin" element={<AvailabilityAdminPage />} />
                      <Route path="/notifications" element={<NotificationCentrePage />} />
                      <Route path="/account" element={<AccountSettingsPage />} />
                      {/*
                        The administration surface. Every one of these screens is ALSO
                        gated server-side — the nav only decides what is offered, never
                        what is permitted (DESIGN_SYSTEM.md §5). A staff member who types
                        the URL gets the screen's own FORBIDDEN state from the API, not a
                        blank page.
                      */}
                      <Route path="/admin/users" element={<UserDirectoryPage />} />
                      <Route path="/admin/users/new" element={<UserCreatePage />} />
                      <Route path="/admin/users/:userId" element={<UserDetailPage />} />
                      <Route path="/admin/users/:userId/sessions" element={<UserSessionsPage />} />
                      <Route path="/admin/org-units" element={<OrgUnitsPage />} />
                      <Route path="/admin/tenants" element={<TenantsPage />} />
                      <Route path="/admin/audit" element={<PlatformAuditPage />} />
                      <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    </Route>
                  </Route>
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </BrowserRouter>
            </ToastProvider>
          </ThemeProvider>
        </LocaleProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
